import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  TrashIcon, 
  ArrowUpIcon, 
  ArrowDownIcon,
  CloudArrowUpIcon,
  ExclamationCircleIcon,
  PencilIcon,
  EyeIcon,
  StarIcon as StarOutlineIcon,
  LinkIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { usePubPackages } from '../../contexts/PubPackageContext';
import { PubPackage } from '../../firebase/pubPackageService';


const PubPackageSchema = Yup.object().shape({
  name: Yup.string()
    .required('Package name is required')
    .max(100, 'Package name is too long'),
  description: Yup.string()
    .required('Description is required')
    .max(200, 'Description is too long'),
  longDescription: Yup.string()
    .required('Long description is required'),
  version: Yup.string()
    .required('Version is required')
    .matches(/^\d+\.\d+\.\d+/, 'Version must be in format x.y.z'),
  pubDevUrl: Yup.string()
    .required('Pub.dev URL is required')
    .url('Must be a valid URL'),
  githubUrl: Yup.string()
    .url('Must be a valid URL'),
  documentationUrl: Yup.string()
    .url('Must be a valid URL'),
  features: Yup.array()
    .of(Yup.string().required('Feature is required'))
    .min(1, 'At least one feature is required'),
  tags: Yup.array()
    .of(Yup.string().required('Tag is required'))
    .min(1, 'At least one tag is required'),
  platforms: Yup.array()
    .of(Yup.string().required('Platform is required'))
    .min(1, 'At least one platform is required'),
  dependencies: Yup.array()
    .of(Yup.string().required('Dependency is required')),
  author: Yup.string()
    .required('Author is required'),
  publishedDate: Yup.string()
    .required('Published date is required'),
});

const initialValues: Omit<PubPackage, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  description: '',
  longDescription: '',
  version: '1.0.0',
  pubDevUrl: '',
  githubUrl: '',
  documentationUrl: '',
  icon: '',
  screenshots: [],
  features: [''],
  tags: [''],
  platforms: [],
  dependencies: [''],
  downloads: 0,
  likes: 0,
  pubPoints: 0,
  popularity: 0,
  maintenanceScore: 0,
  dartSdkConstraint: '>=2.17.0 <4.0.0',
  flutterSdkConstraint: '>=3.0.0',
  publishedDate: new Date().toISOString().split('T')[0],
  lastUpdated: '',
  license: 'MIT',
  author: '',
  featured: false,
  order: 0,
};

const platformOptions = ['web', 'android', 'ios', 'linux', 'macos', 'windows'];

const PubPackagesEditor: React.FC = () => {
  const { pubPackages, loading, error, addPubPackage, editPubPackage, removePubPackage, moveUp, moveDown } = usePubPackages();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<PubPackage | null>(null);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedIconFile, setSelectedIconFile] = useState<File | null>(null);
  const [selectedScreenshotFiles, setSelectedScreenshotFiles] = useState<File[]>([]);
  const iconInputRef = useRef<HTMLInputElement>(null);
  const screenshotInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (values: typeof initialValues, { setSubmitting, resetForm }: any) => {
    try {
      // Add more detailed logging
      console.log('Submitting package data:', values);
      console.log('Screenshots being submitted:', values.screenshots);
      console.log('Screenshots length:', values.screenshots?.length || 0);
      
      if (editingPackage) {
        await editPubPackage(editingPackage.id!, values);
        toast.success('Pub package updated successfully!');
      } else {
        await addPubPackage(values);
        toast.success('Pub package created successfully!');
      }
      setIsModalOpen(false);
      setEditingPackage(null);
      setSelectedIconFile(null);
      setSelectedScreenshotFiles([]);
      resetForm();
    } catch (err: any) {
      console.error('Error saving pub package:', err);
      
      // Show more specific error messages
      let errorMessage = 'Failed to save pub package. Please try again.';
      
      if (err?.message?.includes('Cloudinary')) {
        errorMessage = 'Image upload failed. Please check your network connection and try again.';
      } else if (err?.message?.includes('cloud name')) {
        errorMessage = 'Image upload service not configured properly. Please contact support.';
      } else if (err?.message?.includes('Firebase')) {
        errorMessage = 'Database connection failed. Please check your internet connection.';
      } else if (err?.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your access rights.';
      } else if (err?.message) {
        errorMessage = `Error: ${err.message}`;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (pubPackage: PubPackage) => {
    setEditingPackage(pubPackage);
    setSelectedIconFile(null);
    setSelectedScreenshotFiles([]);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await removePubPackage(id);
      toast.success('Pub package deleted successfully!');
      setDeleteConfirmationId(null);
    } catch (err) {
      console.error('Error deleting pub package:', err);
      toast.error('Failed to delete pub package. Please try again.');
    }
  };

  const handleMoveUp = async (id: string) => {
    try {
      await moveUp(id);
      toast.success('Package moved up!');
    } catch (err) {
      console.error('Error moving package up:', err);
      toast.error('Failed to move package.');
    }
  };

  const handleMoveDown = async (id: string) => {
    try {
      await moveDown(id);
      toast.success('Package moved down!');
    } catch (err) {
      console.error('Error moving package down:', err);
      toast.error('Failed to move package.');
    }
  };

  const handleIconFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Supported image types
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    
    if (supportedTypes.includes(file.type) || file.type.startsWith('image/')) {
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedIconFile(file);
    } else {
      toast.error('Please select a valid image file (JPG, PNG, GIF, WebP, SVG)');
    }
    event.target.value = '';
  };

  const handleScreenshotFilesSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    const validFiles: File[] = [];
    
    for (const file of files) {
      if (supportedTypes.includes(file.type) || file.type.startsWith('image/')) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error(`${file.name}: File size must be less than 5MB`);
          continue;
        }
        validFiles.push(file);
      } else {
        toast.error(`${file.name}: Please select a valid image file`);
      }
    }
    
    setSelectedScreenshotFiles(prev => [...prev, ...validFiles]);
    event.target.value = '';
  };

  const uploadIconFile = async (setFieldValue: any) => {
    if (!selectedIconFile) return;
    
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setFieldValue('icon', result);
        setSelectedIconFile(null);
        setUploading(false);
        toast.success('Icon uploaded successfully!');
      };
      reader.onerror = () => {
        setUploading(false);
        toast.error('Failed to read file. Please try again.');
      };
      reader.readAsDataURL(selectedIconFile);
    } catch (error) {
      setUploading(false);
      toast.error('Failed to upload icon. Please try again.');
    }
  };

  const uploadScreenshotFiles = async (setFieldValue: any, currentValues: any) => {
    console.log('uploadScreenshotFiles called with files:', selectedScreenshotFiles);
    
    if (selectedScreenshotFiles.length === 0) {
      console.log('No files selected, returning early');
      return;
    }
    
    console.log('Starting upload process for', selectedScreenshotFiles.length, 'files');
    setUploading(true);
    
    try {
      console.log('Creating upload promises...');
      const uploadPromises = selectedScreenshotFiles.map((file, index) => {
        console.log(`Creating promise for file ${index + 1}:`, file.name);
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            console.log(`File ${index + 1} (${file.name}) read successfully, data length:`, result?.length || 0);
            resolve(result);
          };
          reader.onerror = () => {
            console.error(`Failed to read file ${index + 1} (${file.name})`);
            reject(new Error(`Failed to read ${file.name}`));
          };
          reader.readAsDataURL(file);
        });
      });
      
      console.log('Waiting for all files to be read...');
      const results = await Promise.all(uploadPromises);
      console.log('All files read successfully. Results count:', results.length);
      console.log('First result preview:', results[0]?.substring(0, 100) + '...');
      
      console.log('About to call setFieldValue...');
      console.log('setFieldValue function type:', typeof setFieldValue);
      
      // Get current screenshots from form values
      const currentScreenshots = Array.isArray(currentValues.screenshots) ? currentValues.screenshots : [];
      console.log('Current screenshots before update:', currentScreenshots);
      console.log('Current screenshots length:', currentScreenshots.length);
      
      const newScreenshots = [...currentScreenshots, ...results];
      console.log('New screenshots array to set:', newScreenshots);
      
      // Use direct field setting (no callback)
      setFieldValue('screenshots', newScreenshots);
      console.log('setFieldValue called with direct value');
      
      console.log('Clearing selected files...');
      setSelectedScreenshotFiles([]);
      setUploading(false);
      console.log('Upload process completed successfully');
      toast.success(`${results.length} screenshot(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error during upload process:', error);
      setUploading(false);
      toast.error('Failed to upload some screenshots. Please try again.');
    }
  };

  const removeSelectedIconFile = () => {
    setSelectedIconFile(null);
  };

  const removeSelectedScreenshotFile = (index: number) => {
    setSelectedScreenshotFiles(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading pub packages</h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Pub Packages</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            Manage your Flutter pub packages to showcase your open source contributions.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              setEditingPackage(null);
              setSelectedIconFile(null);
              setSelectedScreenshotFiles([]);
              setIsModalOpen(true);
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Package
          </button>
        </div>
      </div>

      {/* Packages List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {pubPackages.map((pubPackage, index) => (
            <li key={pubPackage.id} className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  {/* Package Icon */}
                  <div className="flex-shrink-0">
                    {pubPackage.icon ? (
                      <img
                        className="h-12 w-12 rounded-lg object-cover"
                        src={pubPackage.icon}
                        alt={pubPackage.name}
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                        <CodeBracketIcon className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Package Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                        {pubPackage.name}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                        v{pubPackage.version}
                      </span>
                      {pubPackage.featured && (
                        <StarSolidIcon className="h-5 w-5 text-yellow-400" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {pubPackage.description}
                    </p>
                    <div className="flex items-center space-x-4 mt-2">
                      <span className="text-xs text-gray-400">
                        {pubPackage.downloads || 0} downloads
                      </span>
                      <span className="text-xs text-gray-400">
                        {pubPackage.likes || 0} likes
                      </span>
                      <span className="text-xs text-gray-400">
                        {pubPackage.pubPoints || 0} points
                      </span>
                      <span className="text-xs text-gray-400">
                        {pubPackage.platforms?.join(', ')}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2">
                  <a
                    href={pubPackage.pubDevUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    title="View on pub.dev"
                  >
                    <LinkIcon className="h-5 w-5" />
                  </a>
                  
                  <button
                    onClick={() => handleEdit(pubPackage)}
                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={() => handleMoveUp(pubPackage.id!)}
                    disabled={index === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ArrowUpIcon className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={() => handleMoveDown(pubPackage.id!)}
                    disabled={index === pubPackages.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ArrowDownIcon className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={() => setDeleteConfirmationId(pubPackage.id!)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        
        {pubPackages.length === 0 && (
          <div className="text-center py-12">
            <CodeBracketIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No pub packages</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating your first pub package.
            </p>
          </div>
        )}
      </div>

      {/* Modal for Add/Edit Package */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedIconFile(null);
                setSelectedScreenshotFiles([]);
              }}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    {editingPackage ? 'Edit Pub Package' : 'Add New Pub Package'}
                  </h3>
                </div>

                <Formik
                  initialValues={editingPackage ? {
                    ...editingPackage,
                    screenshots: editingPackage.screenshots || [],
                    features: editingPackage.features.length > 0 ? editingPackage.features : [''],
                    tags: editingPackage.tags.length > 0 ? editingPackage.tags : [''],
                    platforms: editingPackage.platforms || [],
                    dependencies: editingPackage.dependencies.length > 0 ? editingPackage.dependencies : [''],
                  } : initialValues}
                  validationSchema={PubPackageSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                  validateOnChange={false}
                  validateOnBlur={false}
                >
                  {({ values, setFieldValue, isSubmitting }) => {
                    console.log('Formik render - current values.screenshots:', values.screenshots);
                    return (
                    <Form className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Information */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Package Name *
                          </label>
                          <Field
                            name="name"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="e.g., my_awesome_package"
                          />
                          <ErrorMessage name="name" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Version *
                          </label>
                          <Field
                            name="version"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="1.0.0"
                          />
                          <ErrorMessage name="version" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Short Description *
                          </label>
                          <Field
                            name="description"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="A brief description of your package"
                          />
                          <ErrorMessage name="description" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Long Description *
                          </label>
                          <Field
                            as="textarea"
                            name="longDescription"
                            rows={4}
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Detailed description with features, use cases, and benefits"
                          />
                          <ErrorMessage name="longDescription" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Pub.dev URL *
                          </label>
                          <Field
                            name="pubDevUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://pub.dev/packages/my_package"
                          />
                          <ErrorMessage name="pubDevUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            GitHub URL
                          </label>
                          <Field
                            name="githubUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://github.com/username/repository"
                          />
                          <ErrorMessage name="githubUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Documentation URL
                          </label>
                          <Field
                            name="documentationUrl"
                            type="url"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="https://docs.example.com"
                          />
                          <ErrorMessage name="documentationUrl" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Author *
                          </label>
                          <Field
                            name="author"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="Your name"
                          />
                          <ErrorMessage name="author" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Package Icon */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Package Icon
                          </label>
                          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            {values.icon ? (
                              <div className="flex items-center space-x-4">
                                <div className="relative">
                                  <img
                                    src={values.icon}
                                    alt="Package icon"
                                    className="h-20 w-20 rounded-lg object-cover"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setFieldValue('icon', '')}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    title="Remove icon"
                                  >
                                    <TrashIcon className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="flex flex-col space-y-2">
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    disabled={uploading}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Change Icon
                                  </button>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF up to 5MB
                                  </p>
                                </div>
                              </div>
                            ) : selectedIconFile ? (
                              <div className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                                      <CloudArrowUpIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                        {selectedIconFile.name}
                                      </p>
                                      <p className="text-xs text-blue-700 dark:text-blue-400">
                                        {(selectedIconFile.size / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={removeSelectedIconFile}
                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                    title="Remove selected file"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => uploadIconFile(setFieldValue)}
                                    disabled={uploading}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload Icon'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Change
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => iconInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select Icon
                                  </button>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF up to 5MB
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={iconInputRef}
                              type="file"
                              accept="image/*,image/gif"
                              className="hidden"
                              onChange={handleIconFileSelect}
                            />
                          </div>
                        </div>

                        {/* Screenshots */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Screenshots & GIFs
                          </label>
                          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                            {Array.isArray(values.screenshots) && values.screenshots.length > 0 ? (
                              <div className="space-y-4">
                                <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                                  {values.screenshots.map((screenshot, index) => (
                                    <div key={index} className="relative group">
                                      <img
                                        src={screenshot}
                                        alt={`Screenshot ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                                        style={{ aspectRatio: '9/16' }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const currentScreenshots = Array.isArray(values.screenshots) ? values.screenshots : [];
                                          const newScreenshots = currentScreenshots.filter((_, i) => i !== index);
                                          setFieldValue('screenshots', newScreenshots);
                                        }}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove screenshot"
                                      >
                                        <TrashIcon className="h-3 w-3" />
                                      </button>
                                      {/* Mobile frame indicator */}
                                      <div className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs px-1 rounded">
                                        📱
                                      </div>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex justify-center pt-4 border-t border-gray-200 dark:border-gray-600">
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select More Screenshots
                                  </button>
                                </div>
                              </div>
                            ) : selectedScreenshotFiles.length > 0 ? (
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  {selectedScreenshotFiles.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                      <div className="flex items-center space-x-3">
                                        <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                                          <CloudArrowUpIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium text-green-900 dark:text-green-300">
                                            {file.name}
                                          </p>
                                          <p className="text-xs text-green-700 dark:text-green-400">
                                            {(file.size / 1024 / 1024).toFixed(2)} MB
                                          </p>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => removeSelectedScreenshotFile(index)}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                        title="Remove selected file"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex space-x-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      console.log('Upload button clicked!');
                                      console.log('Current form values:', values);
                                      uploadScreenshotFiles(setFieldValue, values);
                                    }}
                                    disabled={uploading}
                                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    {uploading ? 'Uploading...' : `Upload ${selectedScreenshotFiles.length} Screenshot${selectedScreenshotFiles.length !== 1 ? 's' : ''}`}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    Add More
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <div className="mt-4">
                                  <button
                                    type="button"
                                    onClick={() => screenshotInputRef.current?.click()}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  >
                                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                                    Select Screenshots
                                  </button>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, GIF, WebP up to 5MB each • Multiple files supported
                                  </p>
                                </div>
                              </div>
                            )}
                            <input
                              ref={screenshotInputRef}
                              type="file"
                              accept="image/*,image/gif"
                              multiple
                              className="hidden"
                              onChange={handleScreenshotFilesSelect}
                            />
                          </div>
                        </div>

                        {/* Platforms */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Supported Platforms *
                          </label>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {platformOptions.map((platform) => (
                              <label key={platform} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  checked={values.platforms && values.platforms.includes(platform)}
                                  onChange={(e) => {
                                    const currentPlatforms = values.platforms || [];
                                    if (e.target.checked) {
                                      setFieldValue('platforms', [...currentPlatforms, platform]);
                                    } else {
                                      setFieldValue('platforms', currentPlatforms.filter(p => p !== platform));
                                    }
                                  }}
                                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                                  {platform}
                                </span>
                              </label>
                            ))}
                          </div>
                          <ErrorMessage name="platforms" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Features */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Features *
                          </label>
                          <FieldArray name="features">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {values.features && values.features.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`features.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="Describe a key feature"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Feature
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="features" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Tags */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Tags *
                          </label>
                          <FieldArray name="tags">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {values.tags && values.tags.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`tags.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., flutter, ui, widgets"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Tag
                                </button>
                              </div>
                            )}
                          </FieldArray>
                          <ErrorMessage name="tags" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        {/* Dependencies */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Dependencies
                          </label>
                          <FieldArray name="dependencies">
                            {({ remove, push }) => (
                              <div className="space-y-2">
                                {values.dependencies && values.dependencies.map((_, index) => (
                                  <div key={index} className="flex items-center space-x-2">
                                    <Field
                                      name={`dependencies.${index}`}
                                      type="text"
                                      className="flex-1 border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                                      placeholder="e.g., flutter/flutter"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => remove(index)}
                                      className="text-red-600 hover:text-red-800"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => push('')}
                                  className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                                >
                                  <PlusIcon className="h-4 w-4 mr-1" />
                                  Add Dependency
                                </button>
                              </div>
                            )}
                          </FieldArray>
                        </div>

                        {/* Statistics */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Downloads
                            </label>
                            <Field
                              name="downloads"
                              type="number"
                              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Likes
                            </label>
                            <Field
                              name="likes"
                              type="number"
                              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Pub Points
                              <span className="text-xs text-gray-500 dark:text-gray-400 block">
                                (Quality score 0-160)
                              </span>
                            </label>
                            <Field
                              name="pubPoints"
                              type="number"
                              min="0"
                              max="160"
                              className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                              placeholder="0"
                            />
                          </div>
                        </div>

                        {/* Constraints */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Dart SDK Constraint
                          </label>
                          <Field
                            name="dartSdkConstraint"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder=">=2.17.0 <4.0.0"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Flutter SDK Constraint
                          </label>
                          <Field
                            name="flutterSdkConstraint"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder=">=3.0.0"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Published Date *
                          </label>
                          <Field
                            name="publishedDate"
                            type="date"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                          />
                          <ErrorMessage name="publishedDate" component="div" className="mt-1 text-sm text-red-600" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            License
                          </label>
                          <Field
                            name="license"
                            type="text"
                            className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            placeholder="MIT"
                          />
                        </div>

                        {/* Featured */}
                        <div className="md:col-span-2">
                          <div className="flex items-center">
                            <Field
                              name="featured"
                              type="checkbox"
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                              Featured Package
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Featured packages will be highlighted on the public page
                              </p>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Modal Actions */}
                      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => {
                            setIsModalOpen(false);
                            setSelectedIconFile(null);
                            setSelectedScreenshotFiles([]);
                          }}
                          className="bg-white dark:bg-gray-700 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Saving...' : editingPackage ? 'Update Package' : 'Create Package'}
                        </button>
                      </div>
                    </Form>
                    );
                  }}
                </Formik>
              </div>
            </div>
          </div>
        )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmationId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setDeleteConfirmationId(null)}
            />

            <div
              className="relative bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Delete Pub Package
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete this pub package? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={() => handleDelete(deleteConfirmationId)}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmationId(null)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

export default PubPackagesEditor; 